import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  BedDouble,
  CalendarPlus,
  ContactRound,
  Dog as DogIcon,
  HeartPulse,
  Home,
  Luggage,
  Moon,
  Pill,
  Scissors,
  ShieldAlert,
  StickyNote,
  Sun,
  Sunrise,
  UserRound,
  Utensils,
  type LucideIcon,
} from 'lucide-react';
import { useState, type ReactNode } from 'react';
import { useForm } from 'react-hook-form';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { z } from 'zod';
import { stayCreateSchema, type Dog, type StayCreate, type StayDogCare } from '../../../../../packages/contracts/src/index.js';
import { Button } from '../../components/ui/button';
import { Card, CardContent, CardHeader } from '../../components/ui/card';
import { FieldError, Input, Label, Textarea } from '../../components/ui/field';
import { CustomerSearchBox } from '../../components/ui/search';
import { DateRangeInputs, DateShortcutButtons, todayDateInput } from '../../components/ui/time-range';
import { api } from '../../shared/api';
import { copy } from '../../shared/i18n';

const emptyStringToUndefined = (value: unknown) => (value === '' ? undefined : value);
export const stayFormSchema = stayCreateSchema.extend({
  kennel: z.preprocess(emptyStringToUndefined, stayCreateSchema.shape.kennel),
  room: z.preprocess(emptyStringToUndefined, stayCreateSchema.shape.room),
  assignedStaffUserId: z.preprocess(emptyStringToUndefined, stayCreateSchema.shape.assignedStaffUserId),
  notes: z.preprocess(emptyStringToUndefined, stayCreateSchema.shape.notes),
});

export type StayFormInput = z.input<typeof stayFormSchema>;
export type StayFormOutput = z.output<typeof stayFormSchema>;

export function StayCreatePage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const queryClient = useQueryClient();
  const initialDate = searchParams.get('date') ?? '';
  const initialCheckOutDate = nextDate(initialDate);
  const initialCustomerId = searchParams.get('customerId') ?? '';
  const initialDogId = searchParams.get('dogId') ?? '';
  const form = useForm<StayFormInput, unknown, StayFormOutput>({
    resolver: zodResolver(stayFormSchema),
    defaultValues: {
      customerId: initialCustomerId,
      dogId: initialDogId,
      dogIds: initialDogId ? [initialDogId] : [],
      checkInDate: initialDate,
      checkOutDate: initialCheckOutDate,
      serviceIds: [],
      dogServiceIds: initialDogId ? { [initialDogId]: [] } : {},
      dogCare: {},
      kennel: '',
      room: '',
      assignedStaffUserId: '',
      notes: '',
    },
  });
  const selectedCustomerId = form.watch('customerId');
  const lookups = useStayLookups(selectedCustomerId);
  const createStay = useMutation({
    mutationFn: (values: StayFormOutput) => api.stays.create(values satisfies StayCreate),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['stays'] });
      await queryClient.invalidateQueries({ queryKey: ['dashboard-summary'] });
      navigate('/admin/stays');
    },
  });

  return (
    <StayFormShell title={copy.stays.createReservation}>
      <form className="grid gap-3" onSubmit={form.handleSubmit((values) => createStay.mutate(values))}>
        <StayFields form={form} {...lookups} />
        {createStay.isError ? <p className="text-sm text-rose-600">{createStay.error.message}</p> : null}
        <Button type="submit" disabled={createStay.isPending}><CalendarPlus className="h-4 w-4" aria-hidden="true" />{copy.stays.createReservation}</Button>
      </form>
    </StayFormShell>
  );
}

function nextDate(value: string) {
  if (!value) return '';
  const date = new Date(`${value}T12:00:00`);
  if (Number.isNaN(date.getTime())) return '';
  date.setDate(date.getDate() + 1);
  return date.toISOString().slice(0, 10);
}

export function useStayLookups(customerId?: string) {
  const dogsQuery = useQuery({
    queryKey: ['customer-dogs', customerId],
    queryFn: () => api.customers.allDogs(customerId ?? ''),
    enabled: Boolean(customerId),
  });
  const servicesQuery = useQuery({ queryKey: ['services'], queryFn: api.services.list });
  return {
    dogs: dogsQuery.data?.items ?? [],
    services: servicesQuery.data?.items ?? [],
  };
}

export function StayFormShell({ title, children }: { title: string; children: ReactNode }) {
  return (
    <div className="max-w-2xl space-y-5">
      <div><h1 className="text-2xl font-semibold text-slate-950">{title}</h1><p className="mt-1 text-sm text-slate-500">{copy.stays.body}</p></div>
      <Card><CardHeader><h2 className="text-sm font-semibold">Datos de hotel</h2></CardHeader><CardContent>{children}</CardContent></Card>
    </div>
  );
}

export function StayFields({ form, customers, dogs, services }: {
  form: ReturnType<typeof useForm<StayFormInput, unknown, StayFormOutput>>;
  customers?: Array<{ id: string; firstName: string; lastName: string; phone?: string; email?: string }>;
  dogs: Dog[];
  services: Array<{ id: string; name: string }>;
}) {
  const selectedCustomerId = form.watch('customerId');
  const selectedDogIds = form.watch('dogIds') ?? [];
  const dogServiceIds = form.watch('dogServiceIds') ?? {};
  const dogCare = form.watch('dogCare') ?? {};
  const [checkInTime, setCheckInTime] = useState('12:00');
  const [checkOutTime, setCheckOutTime] = useState('12:00');
  const toggleDog = (dogId: string) => {
    const dogIds = selectedDogIds.includes(dogId) ? selectedDogIds.filter((id) => id !== dogId) : [...selectedDogIds, dogId];
    const nextDogServiceIds = Object.fromEntries(dogIds.map((id) => [id, dogServiceIds[id] ?? []]));
    const nextDogCare = Object.fromEntries(dogIds.map((id) => { const dog = dogs.find((item) => item.id === id); return [id, dogCare[id] ?? defaultStayCare(dog)]; }));
    form.setValue('dogIds', dogIds, { shouldDirty: true, shouldValidate: true });
    form.setValue('dogId', dogIds[0] ?? '', { shouldDirty: true, shouldValidate: true });
    form.setValue('dogServiceIds', nextDogServiceIds, { shouldDirty: true, shouldValidate: true });
    form.setValue('dogCare', nextDogCare, { shouldDirty: true, shouldValidate: true });
    form.setValue('serviceIds', serviceIdsFromDogMap(nextDogServiceIds), { shouldDirty: true, shouldValidate: true });
  };
  const toggleDogService = (dogId: string, serviceId: string) => {
    const currentServices = dogServiceIds[dogId] ?? [];
    const nextDogServiceIds = {
      ...dogServiceIds,
      [dogId]: currentServices.includes(serviceId) ? currentServices.filter((id) => id !== serviceId) : [...currentServices, serviceId],
    };
    form.setValue('dogServiceIds', nextDogServiceIds, { shouldDirty: true, shouldValidate: true });
    form.setValue('serviceIds', serviceIdsFromDogMap(nextDogServiceIds), { shouldDirty: true, shouldValidate: true });
  };
  return (
    <>
      <div className="grid gap-1.5"><IconLabel icon={UserRound} htmlFor="customerId">{copy.stays.customer}</IconLabel><CustomerSearchBox id="customerId" customers={customers} value={selectedCustomerId} required placeholder={copy.stays.selectCustomer} onChange={(customerId) => { form.setValue('customerId', customerId, { shouldDirty: true, shouldValidate: true }); form.setValue('dogId', ''); form.setValue('dogIds', [], { shouldDirty: true, shouldValidate: true }); form.setValue('serviceIds', [], { shouldDirty: true, shouldValidate: true }); form.setValue('dogServiceIds', {}, { shouldDirty: true, shouldValidate: true }); form.setValue('dogCare', {}, { shouldDirty: true, shouldValidate: true }); }} /><FieldError message={form.formState.errors.customerId?.message} /></div>
      <div className="grid gap-1.5">
        <IconLabel icon={DogIcon}>{copy.stays.dog}</IconLabel>
        <div className="grid gap-2 rounded-md border border-[#d6ded0] bg-white p-3">
          {!selectedCustomerId ? <p className="text-sm text-slate-500">Selecciona un cliente primero.</p> : null}
          {selectedCustomerId && dogs.length === 0 ? <p className="text-sm text-slate-500">Este cliente no tiene perros registrados.</p> : null}
          {dogs.map((dog) => (
            <label key={dog.id} className="flex items-center gap-2 text-sm text-[#334033]">
              <input type="checkbox" checked={selectedDogIds.includes(dog.id)} onChange={() => toggleDog(dog.id)} />
              {dog.name}
            </label>
          ))}
        </div>
        <FieldError message={form.formState.errors.dogId?.message} />
      </div>
      <DateRangeInputs
        mode="datetime"
        start={{
          id: 'checkInDate',
          value: dateTimeValue(form.watch('checkInDate'), checkInTime),
          onChange: (event) => {
            const [date, time] = splitLocalDateTime(event.target.value);
            form.setValue('checkInDate', date, { shouldDirty: true, shouldValidate: true });
            setCheckInTime(time);
          },
        }}
        end={{
          id: 'checkOutDate',
          value: dateTimeValue(form.watch('checkOutDate'), checkOutTime),
          onChange: (event) => {
            const [date, time] = splitLocalDateTime(event.target.value);
            form.setValue('checkOutDate', date, { shouldDirty: true, shouldValidate: true });
            setCheckOutTime(time);
          },
        }}
        startLabel={copy.stays.checkIn}
        endLabel={copy.stays.checkOut}
      >
        <div className="grid gap-2 md:grid-cols-2">
          <DateShortcutButtons unit="days" onToday={() => form.setValue('checkInDate', todayDateInput(), { shouldValidate: true })} />
          <DateShortcutButtons unit="days" start={form.watch('checkInDate')} onToday={() => form.setValue('checkOutDate', todayDateInput(), { shouldValidate: true })} onAdd={(checkOutDate) => form.setValue('checkOutDate', checkOutDate, { shouldValidate: true })} />
        </div>
        <FieldError message={form.formState.errors.checkInDate?.message ?? form.formState.errors.checkOutDate?.message} />
      </DateRangeInputs>
      <div className="grid gap-1.5">
        <IconLabel icon={Scissors}>Servicios por perro</IconLabel>
        <div className="grid gap-3 rounded-md border border-[#d6ded0] bg-white p-3">
          {selectedDogIds.length === 0 ? <p className="text-sm text-slate-500">Selecciona uno o más perros primero.</p> : null}
          {services.length === 0 ? <p className="text-sm text-slate-500">No hay servicios configurados.</p> : null}
          {selectedDogIds.map((dogId) => {
            const dog = dogs.find((item) => item.id === dogId);
            const selectedCount = dogServiceIds[dogId]?.length ?? 0;
            return (
              <details key={dogId} className="rounded-md bg-[#f8faf6] p-3" open={selectedDogIds.length === 1}>
                <summary className="cursor-pointer text-sm font-semibold text-[#334033]">
                  {dog?.name ?? dogId} <span className="font-normal text-[#687461]">({selectedCount} servicio(s))</span>
                </summary>
                <div className="mt-2 grid gap-2">
                {services.map((service) => (
                  <label key={service.id} className="flex items-center gap-2 text-sm text-[#334033]">
                    <input type="checkbox" checked={(dogServiceIds[dogId] ?? []).includes(service.id)} onChange={() => toggleDogService(dogId, service.id)} />
                    {service.name}
                  </label>
                ))}
                </div>
              </details>
            );
          })}
        </div>
        <FieldError message={form.formState.errors.serviceIds?.message} />
      </div>
      <div className="grid gap-2"><IconLabel icon={Utensils}>Alimentación y cuidado por perro</IconLabel>{selectedDogIds.map((dogId) => { const dog = dogs.find((item) => item.id === dogId); if (!dog) return null; return <DogCarePanel key={dogId} dog={dog} value={normalizeStayCare(dogCare[dogId], dog)} onChange={(value) => form.setValue('dogCare', { ...dogCare, [dogId]: value }, { shouldDirty: true, shouldValidate: true })} />; })}</div>
      <div className="grid gap-3 sm:grid-cols-2">
        <div className="grid gap-1.5"><IconLabel icon={Home} htmlFor="kennel">{copy.stays.kennel}</IconLabel><Input id="kennel" {...form.register('kennel')} /></div>
        <div className="grid gap-1.5"><IconLabel icon={BedDouble} htmlFor="room">{copy.stays.room}</IconLabel><Input id="room" {...form.register('room')} /></div>
      </div>
      <div className="grid gap-1.5"><IconLabel icon={StickyNote} htmlFor="notes">{copy.stays.notes}</IconLabel><Textarea id="notes" {...form.register('notes')} /></div>
    </>
  );
}

const feedingOptions = [{ value: 'morning', label: 'Mañana', icon: Sunrise }, { value: 'afternoon', label: 'Tarde', icon: Sun }, { value: 'night', label: 'Noche', icon: Moon }] as const;
const portions = ['0.5', '1', '1.5', '2', '2.5', '3', '4', '5', '6', '7', '8'] as const;

function DogCarePanel({ dog, value, onChange }: { dog: Dog; value: StayDogCare; onChange: (value: StayDogCare) => void }) {
  const patch = (next: Partial<StayDogCare>) => onChange({ ...value, ...next });
  const toggleTime = (time: StayDogCare['feedingTimes'][number]) => patch({ feedingTimes: value.feedingTimes.includes(time) ? value.feedingTimes.filter((item) => item !== time) : [...value.feedingTimes, time] });
  return (
    <details className="rounded-md border border-[#d6ded0] bg-[#fbfcfa]" open>
      <summary className="cursor-pointer px-4 py-3 text-sm font-semibold text-[#223126]">{dog.name} · {ageLabel(dog.birthDate)}</summary>
      <div className="grid gap-4 border-t border-[#e5eadf] p-4">
        <div className="grid gap-2 sm:grid-cols-3">
          {feedingOptions.map(({ value: time, label, icon: Icon }) => (
            <button key={time} type="button" className={`flex h-11 items-center justify-center gap-2 rounded-md border text-sm font-semibold ${value.feedingTimes.includes(time) ? 'border-[#75816a] bg-[#eef3ea] text-[#223126]' : 'border-[#d6ded0] bg-white text-[#687461]'}`} onClick={() => toggleTime(time)}>
              <Icon className="h-4 w-4" aria-hidden="true" />{label}
            </button>
          ))}
        </div>
        <div className="grid gap-2">
          <IconLabel icon={Utensils}>Tamaño de la ración</IconLabel>
          <div className="grid grid-cols-3 gap-2 sm:grid-cols-4 md:grid-cols-6">
            {portions.map((portion) => (
              <button
                key={portion}
                type="button"
                className={`flex h-11 min-w-0 items-center justify-center gap-1.5 rounded-md border px-2 text-sm font-semibold ${value.portionCups === portion ? 'border-[#75816a] bg-[#eef3ea] text-[#223126]' : 'border-[#d6ded0] bg-white text-[#687461]'}`}
                onClick={() => patch({ portionCups: portion })}
                aria-pressed={value.portionCups === portion}
                title={`${portionLabel(portion)} taza(s)`}
              >
                <Utensils className="h-3.5 w-3.5 shrink-0" aria-hidden="true" />
                <PortionLabel value={portion} />
              </button>
            ))}
          </div>
        </div>
        <div className="grid gap-3 sm:grid-cols-2">
          <CareText icon={Utensils} label="Comentarios de alimentación" value={value.feedingComments} onChange={(feedingComments) => patch({ feedingComments })} />
          <CareText icon={Luggage} label="Pertenencias" value={value.belongings} onChange={(belongings) => patch({ belongings })} />
          <CareText icon={ContactRound} label="Contacto de emergencia" value={value.emergencyContact} onChange={(emergencyContact) => patch({ emergencyContact })} />
          <CareText icon={ShieldAlert} label="Alergias" value={value.allergies} onChange={(allergies) => patch({ allergies })} />
          <CareText icon={Pill} label="Medicamento" value={value.medication} onChange={(medication) => patch({ medication })} />
          <CareText icon={HeartPulse} label="Último celo" value={value.lastHeat} onChange={(lastHeat) => patch({ lastHeat })} />
        </div>
        <div className="flex flex-wrap gap-5 text-sm font-medium text-[#4f5d49]">
          <label className="flex items-center gap-2"><input type="checkbox" checked={value.sterilized} onChange={(event) => patch({ sterilized: event.target.checked })} /><HeartPulse className="h-4 w-4" aria-hidden="true" />Esterilizado</label>
          <label className="flex items-center gap-2"><input type="checkbox" checked={value.inHeat} onChange={(event) => patch({ inHeat: event.target.checked })} /><HeartPulse className="h-4 w-4" aria-hidden="true" />Celo</label>
        </div>
      </div>
    </details>
  );
}

function IconLabel({ icon: Icon, children, htmlFor }: { icon: LucideIcon; children: ReactNode; htmlFor?: string }) {
  return <Label htmlFor={htmlFor}><span className="inline-flex items-center gap-2"><Icon className="h-4 w-4 text-[#75816a]" aria-hidden="true" />{children}</span></Label>;
}

function PortionLabel({ value }: { value: string }) {
  const [whole, fraction] = value.split('.');
  if (fraction !== '5') return <span>{whole}</span>;
  return <span className="inline-flex items-center gap-0.5"><span>{whole === '0' ? null : whole}</span><span className="inline-grid text-[10px] leading-none"><span className="border-b border-current px-0.5">1</span><span className="px-0.5">2</span></span></span>;
}

function portionLabel(value: string) {
  const [whole, fraction] = value.split('.');
  return fraction === '5' ? `${whole === '0' ? '' : `${whole} `}1/2` : whole;
}

function CareText({ icon, label, value, onChange }: { icon: LucideIcon; label: string; value?: string; onChange: (value: string) => void }) { return <div className="grid gap-1.5"><IconLabel icon={icon}>{label}</IconLabel><Textarea value={value ?? ''} onChange={(event) => onChange(event.target.value)} /></div>; }
function defaultStayCare(dog?: Dog): StayDogCare { return { feedingTimes: [], sterilized: dog?.sterilized ?? false, allergies: dog?.allergies, inHeat: false, medication: dog?.medications }; }
function normalizeStayCare(value: Partial<StayDogCare> | undefined, dog: Dog): StayDogCare { return { ...defaultStayCare(dog), ...value, feedingTimes: value?.feedingTimes ?? [] }; }
function ageLabel(birthDate?: string) { if (!birthDate) return 'Edad no registrada'; const years = Math.max(0, Math.floor((Date.now() - new Date(`${birthDate}T12:00:00`).getTime()) / 31_557_600_000)); return `${years} año${years === 1 ? '' : 's'}`; }

function serviceIdsFromDogMap(dogServiceIds: Record<string, string[]>) {
  return [...new Set(Object.values(dogServiceIds).flat())];
}

function dateTimeValue(date: string | undefined, time: string) {
  return date ? `${date}T${time}` : '';
}

function splitLocalDateTime(value: string): [string, string] {
  const [date = '', time = '12:00'] = value.split('T');
  return [date, time.slice(0, 5) || '12:00'];
}
